// Path: ./src/utils/banner/bannerPayload.ts
import { CreateGfBannerDto } from "@swagger/typescript-fetch-goflyer";

import {
  BANNER_CONTENT_FIELDS,
  BANNER_LOCALE_SUFFIXES,
  bannerContentField,
} from "./bannerFields";
import { setValidationStartTimeTo1201AM } from "../dateUtil/setTimeTo1201AM";
import { setValidationEndTimeTo1159PM } from "../dateUtil/setTimeTo1159PM";

export const BANNER_CONTENT_FIELD_KEYS = BANNER_CONTENT_FIELDS.flatMap((base) =>
  BANNER_LOCALE_SUFFIXES.map((suffix) => bannerContentField(base, suffix)),
);

export const BANNER_OPTIONAL_STRING_FIELDS = [
  "linkUrl",
  "advertiserName",
  "merchantId",
  "mobileImageId",
] as const;

export const BANNER_CLEARABLE_FIELDS: readonly string[] = [
  "validStartDate",
  "validEndDate",
  ...BANNER_CONTENT_FIELD_KEYS,
  "linkType",
  "linkUrl",
  "advertiserName",
  "merchantId",
  "mobileImageId",
  "targetCenter",
  "targetRadiusKm",
  "targetLocales",
  "targetPlatforms",
  "targetStoreIds",
  "targetCategoryIds",
];

const BANNER_SERVER_OWNED_FIELDS = [
  "id",
  "createdAt",
  "updatedAt",
  "isDeleted",
  "createdByAdminId",
  "createdByAdmin",
  "updatedByAdminId",
  "updatedByAdmin",
  "desktopImage",
  "mobileImage",
];

const isBlank = (value: unknown): boolean =>
  typeof value === "string" && value.trim() === "";

export const buildBannerPayload = (
  banner: CreateGfBannerDto,
  { isUpdate }: { isUpdate: boolean },
): CreateGfBannerDto => {
  const payload: Record<string, unknown> = { ...banner };

  BANNER_CONTENT_FIELD_KEYS.forEach((key) => {
    if (isBlank(payload[key])) payload[key] = undefined;
  });

  BANNER_OPTIONAL_STRING_FIELDS.forEach((key) => {
    if (isBlank(payload[key])) payload[key] = undefined;
  });

  if (!payload.linkUrl) payload.linkType = undefined;

  (
    [
      "targetLocales",
      "targetPlatforms",
      "targetStoreIds",
      "targetCategoryIds",
    ] as const
  ).forEach((key) => {
    const value = payload[key];
    if (Array.isArray(value) && value.length === 0) payload[key] = undefined;
  });

  if (!payload.targetCenter) {
    payload.targetCenter = undefined;
    payload.targetRadiusKm = undefined;
  }

  if (banner.validStartDate) {
    payload.validStartDate = setValidationStartTimeTo1201AM(
      banner.validStartDate,
    );
  }
  if (banner.validEndDate) {
    payload.validEndDate = setValidationEndTimeTo1159PM(banner.validEndDate);
  }

  BANNER_SERVER_OWNED_FIELDS.forEach((key) => delete payload[key]);

  if (isUpdate) {
    BANNER_CLEARABLE_FIELDS.forEach((key) => {
      if (payload[key] === undefined) payload[key] = null;
    });
  } else {
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) delete payload[key];
    });
  }

  return payload as unknown as CreateGfBannerDto;
};
